import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

import { PageHeader } from "@/components/layout/page-header";
import { StatusBadge } from "@/components/data/status-badge";
interface OrgStatus {
  id: string;
  nome: string;
  plano: string;
  modulesEnabled: string[];
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  isTrialActive: boolean;
  hasSubscription: boolean;
  ativo: boolean;
}

type PlanDef = {
  id: 'starter' | 'pro';
  label: string;
  price: string;
  modules: string[];
  description: string;
  highlighted?: boolean;
};

const PLANS: PlanDef[] = [
  {
    id: 'starter',
    label: 'Starter',
    price: 'R$ 99/mês',
    modules: ['Módulo Cacho — mangarás, ciclos, colheitas, relatórios'],
    description: 'Para quem gerencia só a produção.',
  },
  {
    id: 'pro',
    label: 'Pro',
    price: 'R$ 179/mês',
    modules: [
      'Módulo Cacho — mangarás, ciclos, colheitas, relatórios',
      'Módulo Equipe — funcionários, diárias, tarefas, financeiro',
    ],
    description: 'Para quem também gerencia equipe e mão de obra.',
    highlighted: true,
  },
];

function planLabel(plano: string) {
  if (plano === 'pro') return 'Pro';
  if (plano === 'starter') return 'Starter';
  return plano;
}

export default function BillingPage() {
  const [org, setOrg] = useState<OrgStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [redirectingPlan, setRedirectingPlan] = useState<string | null>(null);
  const [location] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === '1') {
      toast({ title: 'Assinatura ativada!', description: 'Seu acesso foi liberado com sucesso.' });
    }
    if (params.get('canceled') === '1') {
      toast({ title: 'Assinatura cancelada', description: 'Você voltou sem assinar.' });
    }
  }, []);

  useEffect(() => {
    fetch('/api/organizations/me', { credentials: 'include' })
      .then(r => r.json())
      .then(setOrg)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [location]);

  const handleCheckout = async (planId: 'starter' | 'pro') => {
    setRedirectingPlan(planId);
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      toast({ title: 'Erro', description: 'Não foi possível iniciar o checkout.', variant: 'destructive' });
      setRedirectingPlan(null);
    }
  };

  const handlePortal = async () => {
    setRedirectingPlan('portal');
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      toast({ title: 'Erro', description: 'Não foi possível abrir o portal.', variant: 'destructive' });
      setRedirectingPlan(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="h-40 rounded-lg bg-muted animate-pulse" />
      </div>
    );
  }

  if (!org) return null;

  const isGrandfathered = !org.trialEndsAt && !org.hasSubscription;
  const trialExpired = org.trialEndsAt && !org.isTrialActive && !org.hasSubscription;
  const needsCheckout = (org.isTrialActive && !org.hasSubscription && org.trialEndsAt) || trialExpired;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <PageHeader titulo="Assinatura" descricao={org.nome} />

      {/* Status atual */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Rizoma Farm</CardTitle>
            {/* Situação da conta é ESTADO — os quatro casos pelo mesmo componente. */}
            {isGrandfathered && <StatusBadge tom="neutro">Conta ativa</StatusBadge>}
            {org.hasSubscription && <StatusBadge tom="sucesso" ponto>Assinatura ativa — {planLabel(org.plano)}</StatusBadge>}
            {org.isTrialActive && !org.hasSubscription && org.trialEndsAt && (
              <StatusBadge tom="info" ponto>Trial — {org.trialDaysLeft} dias restantes</StatusBadge>
            )}
            {trialExpired && <StatusBadge tom="erro" ponto>Trial encerrado</StatusBadge>}
          </div>
          {org.hasSubscription && (
            <CardDescription>
              Módulos ativos: {(org.modulesEnabled ?? []).join(', ')}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {isGrandfathered && (
            <p className="text-sm text-muted-foreground">
              Sua conta tem acesso completo ao Rizoma Farm.
            </p>
          )}

          {org.isTrialActive && !org.hasSubscription && org.trialEndsAt && (
            <p className="text-sm text-muted-foreground">
              Seu trial vai até{' '}
              <strong>{new Date(org.trialEndsAt).toLocaleDateString('pt-BR')}</strong>.
              {' '}Escolha um plano abaixo para continuar após essa data.
            </p>
          )}

          {trialExpired && (
            <p className="text-sm text-erro">
              Seu trial encerrou. Escolha um plano abaixo para retomar o acesso. Seus dados estão seguros.
            </p>
          )}

          {org.hasSubscription && (
            <>
              <p className="text-sm text-muted-foreground">
                Sua assinatura está ativa. Gerencie pagamentos, histórico, upgrade ou cancelamento pelo portal.
              </p>
              <Button variant="outline" className="w-full" onClick={handlePortal} disabled={!!redirectingPlan}>
                {redirectingPlan === 'portal' ? 'Aguarde...' : 'Gerenciar assinatura'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Seleção de plano (trial ativo, trial expirado) */}
      {needsCheckout && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Escolha seu plano</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PLANS.map((plan) => (
              <Card
                key={plan.id}
                className={plan.highlighted ? 'border-2 border-primary' : undefined}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{plan.label}</CardTitle>
                    {plan.highlighted && <Badge>Recomendado</Badge>}
                  </div>
                  <div className="text-2xl font-bold">{plan.price}</div>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ul className="text-sm space-y-1.5">
                    {plan.modules.map((m) => (
                      <li key={m} className="flex items-start gap-2">
                        <span className="text-sucesso font-bold mt-0.5">✓</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={plan.highlighted ? 'default' : 'outline'}
                    onClick={() => handleCheckout(plan.id)}
                    disabled={!!redirectingPlan}
                  >
                    {redirectingPlan === plan.id ? 'Aguarde...' : `Assinar ${plan.label}`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Cupons de desconto podem ser aplicados no checkout. Cancele quando quiser.
          </p>
        </div>
      )}
    </div>
  );
}
